import assign from 'lodash/assign';
import each from 'lodash/each';
import extend from 'lodash/extend';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import reject from 'lodash/reject';
import uuid from 'uuid/v4';
import store from '@Store';
import Auth from '@Services/Auth';
import { SearchTypes } from '@Constants/CommonConstants';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getSearchFromStore, setReactInputValue } from '@Containers/QuickSearchAutosuggest';
import { dispatchSetActiveResults } from '@Containers/Header/HeaderComponentActions';
import { dispatchOpenImportFileModal } from '@Containers/Search/Shared/ImportFile/ImportFileModalActions';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import MarvinHelpers from '@Services/MarvinHelpers';
import * as QueryValidation from '@Services/QueryValidation';
import Search from '@Services/Search';
import UserDatabases from '@Services/UserDatabases';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import NewRelicService, { BROWSER_INTERACTIONS_NAMES } from '@Services/NewRelicService';
import StructureContainers from '@Services/StructureContainers';
import SearchStorage from '@Services/SearchStorage';
import { resetState as resetStateQuickResults } from '@Pages/QuickSearch/Components/QuickResults/QuickResultsActions';
import { resetState as resetStateResultsPreview } from '@Containers/ResultsPreview/actions';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';
import { QUICK_SEARCH_CACHE_FORMS, QUICK_SEARCH_CACHE_NAME, QUICK_SEARCH_STRUCTURE_UID } from '../../QuickSearchConstants';
import {
  buildQuerySetQueryReducerType,
  buildQuerySetIsFormsButtonsEnabledReducerType,
  buildQuerySetIsClassicLicenseReducerType,
  buildQuerySetStructurePreviewReducerType,
  buildQuerySetStructureNameReducerType,
  buildQuerySetPreviewSizeReducerType,
  buildQuerySetDbsReducerType,
  buildQuerySetQsFormsReducerType,
  buildQueryResetStateReducerType,
} from './BuildQueryConstants';
import {
  query as querySelector,
  structureName as structureNameSelector,
  structurePreview as structurePreviewSelector,
} from './BuildQuerySelectors';

const SEARCH_MIN_LENGTH = 2;

export function setQuery(query) {
  return {
    type: buildQuerySetQueryReducerType,
    payload: { query },
  };
}

export function setIsFormsButtonsEnabled(isFormsButtonsEnabled) {
  return {
    type: buildQuerySetIsFormsButtonsEnabledReducerType,
    payload: { isFormsButtonsEnabled },
  };
}

export function setIsClassicLicense(isClassicLicense) {
  return {
    type: buildQuerySetIsClassicLicenseReducerType,
    payload: { isClassicLicense },
  };
}

export function setStructurePreview(structurePreview) {
  return {
    type: buildQuerySetStructurePreviewReducerType,
    payload: { structurePreview },
  };
}

export function setStructureName(structureName) {
  return {
    type: buildQuerySetStructureNameReducerType,
    payload: { structureName },
  };
}

export function setPreviewSize(previewSize) {
  return {
    type: buildQuerySetPreviewSizeReducerType,
    payload: { previewSize },
  };
}

export function setDbs(dbs) {
  return {
    type: buildQuerySetDbsReducerType,
    payload: { dbs },
  };
}

export function setQsForms(qsForms) {
  return {
    type: buildQuerySetQsFormsReducerType,
    payload: { qsForms },
  };
}

export function resetState() {
  return {
    type: buildQueryResetStateReducerType,
  };
}

let structureContainer = {};
let skipNextBracketsQuotesCheck;

/**
 * @name initialize
 * @description Bootstrap function for current controller
 */
export const initialize = () => async (dispatch) => {
  // Reset previous quick results
  dispatch(resetStateQuickResults());

  // Reset previous quick results - when Substance Result Optimization enabled
  dispatch(resetStateResultsPreview());

  dispatch(resetState());
  dispatch(setIsFormsButtonsEnabled(Auth.isFeatureEnabled(FEATURES.displayQuickSearchFormsButtons)));
  dispatch(setIsClassicLicense(Auth.isClassicLicense()));
  dispatch(setDbs(UserDatabases.getSeparatedDatabases()));

  await StructureContainers.getContainerByUID(QUICK_SEARCH_STRUCTURE_UID)
    .then((container) => {
      structureContainer = container;
      if (!structureContainer.name) {
        structureContainer.name = {
          name: parseV3000SumFormula(structureContainer.data),
          isLinearFormula: true,
        };
      }

      dispatch(setStructurePreview(structureContainer.preview));
      dispatch(setStructureName(structureContainer.name));

      if (structureContainer.data) {
        dispatch(setPreviewSize(MarvinHelpers.getParams(MarvinHelpers.getBoundCount(structureContainer.data, 4))));
      }
    })
    .catch(Logger.error);

  const license = Auth.getLicense(true);
  const licenseQsForms = QUICK_SEARCH_CACHE_FORMS[license];

  const qsForms = licenseQsForms.map((form, formId) => assign({}, form, { formId }));
  dispatch(setQsForms(qsForms));

  await Search.getForms()
    .then((formGroups) => {
      const reaxysForms = formGroups.reduce((acc, group) => acc.concat(group.children), []);
      // eslint-disable-next-line no-shadow
      const qsForms = [];

      each(licenseQsForms, (qsForm) => {
        const formData = find(reaxysForms, (form) => qsForm.title === form.displayName);
        if (formData) {
          const preparedForm = assign({}, qsForm, { formId: formData.formId });
          qsForms.push(preparedForm);
        }
      });

      dispatch(setQsForms(qsForms));
    })
    .catch(Logger.error);
};

export const getQuery = (query) => {
  if (query?.text?.trim().length < SEARCH_MIN_LENGTH) {
    // eslint-disable-next-line no-param-reassign
    query.text = null;
  }
  if (query && query.Typeaheads) {
    // eslint-disable-next-line no-param-reassign
    query.Typeaheads = reject(query.Typeaheads, (typeahead) => !has(typeahead, 'Category'));
  }
  return query;
};

/**
 * @name saveResults
 * @description Save our results in DataSaver service
 */
export const saveResults = () => (dispatch, getState) => {
  const query = querySelector(getState());
  const structurePreview = structurePreviewSelector(getState());
  AppStorage.instance.set(QUICK_SEARCH_CACHE_NAME, {
    structurePreview,
    query: getQuery(query),
  });
};

export const clearStructure = () => (dispatch) => {
  // dispatch event since sticky header directive should recalculate layout for sticky header
  window.dispatchEvent(new CustomEvent('resize'));
  StructureContainers.removeContainerByUID(QUICK_SEARCH_STRUCTURE_UID);
  structureContainer = {};
  dispatch(setStructurePreview(null));
  dispatch(setStructureName(null));
  dispatch(saveResults());
};

export const goToQueryBuilderForm = (form) => () => {
  const { formId, title } = form;
  Catalyst.trackContentLinkClick(title, 'form', `${Catalyst.getPrefix()}:form:${title}`);
  history.push({
    pathname: `/search/advanced`,
    state: { importFormId: formId },
  });
};

export const goToStructure = () => () => {
  history.push({
    pathname: `/structure-editor/${QUICK_SEARCH_STRUCTURE_UID}`,
    state: { structureUID: QUICK_SEARCH_STRUCTURE_UID },
  });
};

export const populateQuickSearch = (populatedTerm) => (dispatch, getState) => {
  const query = querySelector(getState());
  dispatch(setQuery({ ...query, text: populatedTerm }));
  setReactInputValue(populatedTerm);
};

export const updateInputText = (value) => (dispatch, getState) => {
  const query = querySelector(getState());
  const updatedQuery = { ...query, text: value };
  dispatch(setQuery(updatedQuery));
  setReactInputValue(value);

  if (updatedQuery && updatedQuery.Typeaheads) {
    updatedQuery.Typeaheads = reject(updatedQuery.Typeaheads, (typeahead) => !has(typeahead, 'Category'));
  }
  const cache = AppStorage.instance.get(QUICK_SEARCH_CACHE_NAME) || {};
  AppStorage.instance.set(QUICK_SEARCH_CACHE_NAME, extend(cache, { query: updatedQuery }));
};

function searchBuQueryisStructureData(queryText, structureContainerData, structurePreview, Typeaheads, analytics) {
  AppStorage.instance.set(QUICK_SEARCH_CACHE_NAME, {
    structurePreview,
    query: {
      text: querySelector(store.getState())?.text ?? '',
    },
  });

  const search = {
    query: querySelector(store.getState())?.text ?? '',
    structure: structureContainerData.data,
    options: structureContainerData.options || [{ name: 'searchBy', value: 'exact' }],
    structurePreview,
    searchType: SearchTypes.QUICK_SEARCH,
    // search storage item will be extended with structure and this can impact on search re-runs
    structureUsed: !!structureContainerData.data,
  };

  if (!isEmpty(Typeaheads)) {
    search.Typeaheads = Typeaheads;
  }

  // set or reset
  if (isEmpty(analytics)) {
    Catalyst.resetAutosuggest();
  } else {
    Catalyst.setSearchResults(analytics, true);
  }
  Catalyst.updateSearchObject({
    details: QueryValidation.validateIsPhraseSearch(queryText) ? 'phrase:true' : null,
  });

  Logger.info('Search query:', search);
  SearchStorage.setNew(search)
    .then((searchId) => {
      const cache = AppStorage.instance.get(QUICK_SEARCH_CACHE_NAME) || {};
      AppStorage.instance.set(QUICK_SEARCH_CACHE_NAME, extend(cache, { searchId }));
      // disable Results menu item to prevent navigation to old results using general menu before click view results
      dispatchSetActiveResults(false);
      history.push({
        pathname: paths.PREVIEW,
        state: { searchId: cache.searchId, action: 'preview' },
      });
    })
    .catch(Logger.error);
}

export const searchByQuery = () => async (dispatch, getState) => {
  const structurePreview = structurePreviewSelector(getState());
  const { queryText, Typeaheads, analytics } = getSearchFromStore();
  NewRelicService.createBrowserInteraction(BROWSER_INTERACTIONS_NAMES.TIME_TO_FIND_SUBSTANCES);

  await StructureContainers.getContainerByUID(QUICK_SEARCH_STRUCTURE_UID)
    .then((container) => {
      structureContainer = container;

      if (queryText) {
        // Validate language
        if (!QueryValidation.validateLanguage(queryText)) {
          QueryValidation.showIncorrectLanguageMessage();
          return;
        }

        // Validate brackets and quotes
        if (!skipNextBracketsQuotesCheck) {
          if (!QueryValidation.validateBrackets(queryText) || !QueryValidation.validateQuotes(queryText)) {
            QueryValidation.showIncorrectBracketsOrQuotesMessage({
              onConfirm: () => {
                skipNextBracketsQuotesCheck = true;
                dispatch(searchByQuery());
              },
            });
            return;
          }
        } else {
          skipNextBracketsQuotesCheck = false;
        }
      }

      searchBuQueryisStructureData(queryText, structureContainer, structurePreview, Typeaheads, analytics);
    })
    .catch(Logger.error);
};

export const showImportModal = () => () => {
  const sections = [Catalyst.getPrefix(), 'quicksearch', 'import'];
  Catalyst.trackProductFeature(sections.join(':'));
  dispatchOpenImportFileModal();
};

export const showStructurePopup = () => (dispatch, getState) => {
  const structurePreview = structurePreviewSelector(getState());
  const structureName = structureNameSelector(getState());

  dispatch(openStructureDialog(uuid(), structurePreview, structureName));
};
